// Client detail (SPEC §4): contact card, computed LTV/visits, acquisition
// selector (set_acquisition RPC), tags editor, notes CRUD, package panel with
// full ledger history, chronological sale history (append-only — corrections
// only via the add_adjustment RPC), rebook snooze, and the message thread.

import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import Modal from "../components/Modal";
import TagChips from "../components/TagChips";
import MessageThread from "../components/MessageThread";
import MarkReachedOut, {
  TouchChannelBadge,
  daysAgoLabel,
} from "../components/MarkReachedOut";
import {
  DueBadge,
  PackageBadge,
  RetentionDot,
  SegmentChip,
  SourceBadge,
  WhaleBadge,
} from "../components/Badges";
import { mailtoHref, money, phoneDisplay, smsHref, telHref } from "../lib/format";
import { fmtDate, fmtDateTime } from "../lib/dates";
import { retentionColor } from "../lib/retention";
import type {
  AcquisitionSource,
  ClientRow,
  Credit,
  Id,
  Note,
  PackageLedgerEntry,
  Reminder,
  Sale,
  Touch,
} from "../lib/types";

const SOURCE_OPTIONS: { value: AcquisitionSource; label: string }[] = [
  { value: "google_ads", label: "Google Ads" },
  { value: "seo_gbp", label: "SEO (GBP)" },
  { value: "referral", label: "Referral" },
  { value: "organic", label: "Organic" },
  { value: "other", label: "Other" },
];

const KIND_LABELS: Record<string, string> = {
  service: "Service",
  package_purchase: "Package purchase",
  package_redemption: "Redemption",
  package_addon: "Add-on",
  refund: "Refund",
  adjustment: "Adjustment",
};

const REASON_LABELS: Record<string, string> = {
  grant: "Grant",
  redemption: "Redemption",
  manual_adjustment: "Manual adjustment",
};

/** PostgREST can serialize numerics as strings; normalize before math. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isNaN(n) ? null : n;
}

/** Today's business date (YYYY-MM-DD, America/New_York). */
function nyTodayYmd(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function addWeeksYmd(ymd: string, weeks: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + weeks * 7)).toISOString().slice(0, 10);
}

/**
 * True only when a note was actually edited. Some inserts stamp updated_at on
 * creation, so require it to trail created_at by more than ~2 seconds.
 */
function noteEdited(n: Note): boolean {
  if (!n.updated_at) return false;
  const created = new Date(n.created_at).getTime();
  const updated = new Date(n.updated_at).getTime();
  if (Number.isNaN(created) || Number.isNaN(updated)) return false;
  return updated - created > 2000;
}

interface ClientExtra {
  acquisition_note: string | null;
}

/**
 * Fallback row fetched straight from `clients` when v_client_list has no row
 * for the id: either the client was merged away (merged_into_client_id set)
 * or they're an 'internal'-tagged account (nurses, test numbers) that the
 * canonical view deliberately hides.
 */
interface ClientStub {
  id: Id;
  first_name: string | null;
  last_name: string | null;
  phone_e164: string | null;
  email: string | null;
  merged_into_client_id: Id | null;
}

interface ClientFetch {
  row: ClientRow | null;
  extra: ClientExtra | null;
  stub: ClientStub | null;
}

interface PackageRow {
  id: Id;
  client_id: Id;
  drips_total: number;
  purchase_sale_id: Id | null;
  active: boolean;
}

interface PackagesFetch {
  packages: PackageRow[];
  ledger: PackageLedgerEntry[];
}

const SALE_COLUMNS: Column<Sale>[] = [
  {
    key: "sale_date",
    label: "Date",
    width: "130px",
    render: (s) => <span className="nowrap">{fmtDate(s.sale_date)}</span>,
    sortValue: (s) => s.sale_date,
  },
  {
    key: "kind",
    label: "Kind",
    render: (s) => (
      <span className={`badge badge-${s.kind}`}>{KIND_LABELS[s.kind] ?? s.kind}</span>
    ),
    sortValue: (s) => s.kind,
  },
  {
    key: "service_name",
    label: "Service",
    render: (s) => s.service_name ?? <span className="muted">—</span>,
  },
  {
    key: "pay_status",
    label: "Status",
    render: (s) => (s.pay_status ? <span className="muted">{s.pay_status}</span> : null),
    sortValue: (s) => s.pay_status,
  },
  {
    key: "sale_total",
    label: "Collected",
    align: "right",
    render: (s) => {
      const v = num(s.sale_total) ?? 0;
      return (
        <span className={v < 0 ? "red nowrap" : "nowrap"}>
          {money(v)}
          {s.kind === "package_redemption" && <span className="muted small"> · −1 pkg</span>}
        </span>
      );
    },
    sortValue: (s) => num(s.sale_total),
  },
];

export default function ClientDetail() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? "";
  const [searchParams] = useSearchParams();
  const compose = searchParams.get("compose") === "1";

  // ---- data ----
  const clientQ = useQuery<ClientFetch>(async () => {
    const { data, error } = await supabase
      .from("v_client_list")
      .select("*")
      .eq("id", id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (data) {
      const { data: extra } = await supabase
        .from("clients")
        .select("acquisition_note")
        .eq("id", id)
        .maybeSingle();
      return { row: data as ClientRow, extra: extra as ClientExtra | null, stub: null };
    }
    // Not in the canonical list — merged away, an internal account, or a bad id.
    const { data: stub, error: stubError } = await supabase
      .from("clients")
      .select("id, first_name, last_name, phone_e164, email, merged_into_client_id")
      .eq("id", id)
      .maybeSingle();
    if (stubError) throw new Error(stubError.message);
    return { row: null, extra: null, stub: stub as ClientStub | null };
  }, [id]);

  const salesQ = useQuery<Sale[]>(async () => {
    const { data, error } = await supabase
      .from("sales")
      .select("*")
      .eq("client_id", id)
      .order("sale_date", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as Sale[];
  }, [id]);

  const packagesQ = useQuery<PackagesFetch>(async () => {
    const { data, error } = await supabase.from("packages").select("*").eq("client_id", id);
    if (error) throw new Error(error.message);
    const packages = (data ?? []) as PackageRow[];
    if (packages.length === 0) return { packages, ledger: [] };
    const pkgIds = packages.map((p) => p.id);
    let led = await supabase
      .from("package_ledger")
      .select("*")
      .in("package_id", pkgIds)
      .order("created_at", { ascending: false });
    if (led.error) {
      // created_at is unconfirmed on this pre-existing table; fall back unordered.
      led = await supabase.from("package_ledger").select("*").in("package_id", pkgIds);
    }
    if (led.error) throw new Error(led.error.message);
    return { packages, ledger: (led.data ?? []) as PackageLedgerEntry[] };
  }, [id]);

  const remindersQ = useQuery<Reminder[]>(async () => {
    const { data, error } = await supabase
      .from("client_reminders")
      .select("*")
      .eq("client_id", id)
      .order("due_date", { ascending: true });
    if (error) throw new Error(error.message);
    return (data ?? []) as Reminder[];
  }, [id]);

  const touchesQ = useQuery<Touch[]>(async () => {
    const { data, error } = await supabase
      .from("outreach_touches")
      .select("*")
      .eq("client_id", id)
      .order("touched_at", { ascending: false })
      .limit(12);
    if (error) throw new Error(error.message);
    return (data ?? []) as Touch[];
  }, [id]);

  // Outreach credits (client_credits, 014) — promised on queue sends, resolved
  // here via the redeem_credit RPC.
  const creditsQ = useQuery<Credit[]>(async () => {
    const { data, error } = await supabase
      .from("client_credits")
      .select("*")
      .eq("client_id", id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as Credit[];
  }, [id]);

  const notesQ = useQuery<Note[]>(async () => {
    const { data, error } = await supabase
      .from("client_notes")
      .select("*")
      .eq("client_id", id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as Note[];
  }, [id]);

  // ---- local state ----
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [srcDraft, setSrcDraft] = useState("");
  const [srcNote, setSrcNote] = useState("");
  const [snoozeDraft, setSnoozeDraft] = useState("");

  const [noteDraft, setNoteDraft] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");

  const [adjOpen, setAdjOpen] = useState(false);
  const [adjAmount, setAdjAmount] = useState("");
  const [adjNote, setAdjNote] = useState("");
  const [adjDate, setAdjDate] = useState("");

  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgTotal, setPkgTotal] = useState("");
  const [pkgUsed, setPkgUsed] = useState("0");
  const [pkgNote, setPkgNote] = useState("");

  const [remDate, setRemDate] = useState("");
  const [remNote, setRemNote] = useState("");

  const client = clientQ.data?.row ?? null;

  useEffect(() => {
    if (clientQ.data?.row) {
      setSrcDraft(clientQ.data.row.acquisition_source ?? "");
      setSrcNote(clientQ.data.extra?.acquisition_note ?? "");
      setSnoozeDraft(clientQ.data.row.rebook_snoozed_until ?? "");
    }
  }, [clientQ.data]);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // ---- mutations ----
  function addTag(tag: string) {
    return run(async () => {
      const { error } = await supabase
        .from("client_tags")
        .upsert({ client_id: id, tag }, { onConflict: "client_id,tag", ignoreDuplicates: true });
      if (error) throw new Error(error.message);
      clientQ.reload();
    });
  }

  function removeTag(tag: string) {
    return run(async () => {
      const { error } = await supabase
        .from("client_tags")
        .delete()
        .eq("client_id", id)
        .eq("tag", tag);
      if (error) throw new Error(error.message);
      clientQ.reload();
    });
  }

  function applyAcquisition() {
    void run(async () => {
      // p_set_note:true makes the RPC apply p_note as-given (null clears the
      // note). The Clients bulk action omits it, leaving notes untouched.
      const { error } = await supabase.rpc("set_acquisition", {
        p_client_ids: [id],
        p_source: srcDraft || null,
        p_note: srcNote.trim() || null,
        p_set_note: true,
      });
      if (error) throw new Error(error.message);
      clientQ.reload();
    });
  }

  function setSnooze(value: string | null) {
    void run(async () => {
      const { error } = await supabase
        .from("clients")
        .update({ rebook_snoozed_until: value })
        .eq("id", id);
      if (error) throw new Error(error.message);
      clientQ.reload();
    });
  }

  function addNote() {
    const body = noteDraft.trim();
    if (!body) return;
    void run(async () => {
      const { error } = await supabase.from("client_notes").insert({ client_id: id, body });
      if (error) throw new Error(error.message);
      setNoteDraft("");
      notesQ.reload();
    });
  }

  function saveNoteEdit(noteId: Id) {
    const body = editDraft.trim();
    if (!body) return;
    void run(async () => {
      const { error } = await supabase
        .from("client_notes")
        .update({ body, updated_at: new Date().toISOString() })
        .eq("id", noteId);
      if (error) throw new Error(error.message);
      setEditingNoteId(null);
      setEditDraft("");
      notesQ.reload();
    });
  }

  function deleteNote(noteId: Id) {
    if (!window.confirm("Delete this note?")) return;
    void run(async () => {
      const { error } = await supabase.from("client_notes").delete().eq("id", noteId);
      if (error) throw new Error(error.message);
      notesQ.reload();
    });
  }

  function submitAdjustment() {
    const amount = Number(adjAmount);
    const note = adjNote.trim();
    if (!note || !Number.isFinite(amount) || amount === 0) {
      setActionError("Adjustment needs a non-zero amount and a note.");
      return;
    }
    void run(async () => {
      const rpcParams: Record<string, unknown> = {
        p_client_id: id,
        p_amount: amount,
        p_note: note,
      };
      // Noon UTC always lands on the same New York calendar date.
      if (adjDate) rpcParams.p_sale_date = `${adjDate}T12:00:00Z`;
      const { error } = await supabase.rpc("add_adjustment", rpcParams);
      if (error) throw new Error(error.message);
      setAdjOpen(false);
      setAdjAmount("");
      setAdjNote("");
      setAdjDate("");
      salesQ.reload();
      clientQ.reload();
    });
  }

  /** clients.id is integer in the live DB; send a number when the param is numeric. */
  const idArg: Id = /^\d+$/.test(id) ? Number(id) : id;

  function submitPackage() {
    const total = Number(pkgTotal);
    const used = Number(pkgUsed || "0");
    if (!Number.isInteger(total) || total < 1) {
      setActionError("Drips total must be a whole number ≥ 1.");
      return;
    }
    if (!Number.isInteger(used) || used < 0 || used > total) {
      setActionError("Already-used drips must be between 0 and the total.");
      return;
    }
    void run(async () => {
      // add_manual_package inserts the package + ledger grant (and a
      // manual_adjustment for already-used drips). It NEVER touches sales.
      const { error } = await supabase.rpc("add_manual_package", {
        p_client_id: idArg,
        p_drips_total: total,
        p_drips_used: used,
        p_note: pkgNote.trim() || null,
      });
      if (error) throw new Error(error.message);
      setPkgOpen(false);
      setPkgTotal("");
      setPkgUsed("0");
      setPkgNote("");
      packagesQ.reload();
      clientQ.reload();
    });
  }

  function addReminder() {
    const note = remNote.trim();
    if (!note || !remDate) return;
    void run(async () => {
      const { error } = await supabase.from("client_reminders").insert({
        client_id: idArg,
        due_date: remDate,
        note,
        source: "manual",
      });
      if (error) throw new Error(error.message);
      setRemNote("");
      setRemDate("");
      remindersQ.reload();
    });
  }

  function completeReminder(reminderId: string, status: "done" | "dismissed") {
    void run(async () => {
      const { error } = await supabase.rpc("complete_reminder", {
        p_id: reminderId,
        p_status: status,
      });
      if (error) throw new Error(error.message);
      remindersQ.reload();
    });
  }

  /** Resolve an active credit via the redeem_credit RPC (014). */
  function resolveCredit(creditId: string, status: "redeemed" | "revoked") {
    void run(async () => {
      const { error } = await supabase.rpc("redeem_credit", {
        p_credit_id: creditId,
        p_status: status,
      });
      if (error) throw new Error(error.message);
      creditsQ.reload();
    });
  }

  // ---- render guards ----
  if (clientQ.loading) {
    return (
      <div className="page">
        <div className="loading">
          <span className="spinner" /> Loading client…
        </div>
      </div>
    );
  }

  if (clientQ.error) {
    return (
      <div className="page">
        <Link to="/clients">← Clients</Link>
        <div className="error-banner mt-1">Failed to load client: {clientQ.error}</div>
      </div>
    );
  }

  if (!client) {
    const stub = clientQ.data?.stub ?? null;
    if (stub?.merged_into_client_id != null) {
      return (
        <div className="page">
          <Link to="/clients">← Clients</Link>
          <div className="info-banner mt-1">
            {[stub.first_name, stub.last_name].filter(Boolean).join(" ") || "This client"} was
            merged into another record.{" "}
            <Link to={`/clients/${stub.merged_into_client_id}`}>Open the canonical client</Link>.
          </div>
        </div>
      );
    }
    if (stub) {
      // Exists in `clients` but hidden from v_client_list: an 'internal'-tagged
      // account (nurse, test number). Render a minimal page so Inbox links to
      // these accounts don't dead-end.
      const stubName =
        [stub.first_name, stub.last_name].filter(Boolean).join(" ") ||
        `Client ${String(stub.id)}`;
      return (
        <div className="page">
          <Link to="/clients">← Clients</Link>
          <div className="page-header mt-1">
            <div>
              <h1 className="page-title">{stubName}</h1>
              <div className="mt-1">
                <span className="badge badge-muted">Internal account</span>
              </div>
              <div className="page-sub">
                Hidden from the client list, metrics, segments, and outreach.
              </div>
            </div>
          </div>
          <div className="card">
            <div className="panel-title">Contact</div>
            <dl className="kv">
              <dt>Phone</dt>
              <dd>
                {stub.phone_e164 ? (
                  <span className="nowrap">
                    <a href={telHref(stub.phone_e164)}>{phoneDisplay(stub.phone_e164)}</a>
                    {" · "}
                    <a href={smsHref(stub.phone_e164)}>SMS</a>
                  </span>
                ) : (
                  <span className="muted">—</span>
                )}
              </dd>
              <dt>Email</dt>
              <dd>
                {stub.email ? (
                  <a href={mailtoHref(stub.email)}>{stub.email}</a>
                ) : (
                  <span className="muted">—</span>
                )}
              </dd>
            </dl>
          </div>
        </div>
      );
    }
    return (
      <div className="page">
        <Link to="/clients">← Clients</Link>
        <div className="empty-state">Client not found.</div>
      </div>
    );
  }

  const weeks = num(client.weeks_since_last_visit);
  const medianDays = num(client.median_gap_days);
  const gapWeeks = medianDays != null && medianDays > 0 ? Math.max(1, Math.round(medianDays / 7)) : null;
  const pendingReminders = (remindersQ.data ?? []).filter((r) => r.status === "pending");
  const doneReminders = (remindersQ.data ?? []).filter((r) => r.status !== "pending");
  const snoozed =
    client.rebook_snoozed_until != null && client.rebook_snoozed_until >= nyTodayYmd();
  const today = nyTodayYmd();
  const acquisitionDirty =
    srcDraft !== (client.acquisition_source ?? "") ||
    srcNote.trim() !== (clientQ.data?.extra?.acquisition_note ?? "");

  return (
    <div className="page">
      <Link to="/clients">← Clients</Link>

      <div className="page-header mt-1">
        <div>
          <h1 className="page-title">
            <RetentionDot color={retentionColor(client)} /> {client.full_name}
          </h1>
          <div className="mt-1">
            <SegmentChip segment={client.rfm_segment} link />
            <WhaleBadge whale={client.is_whale} />
            <PackageBadge balance={num(client.active_package_balance)} />
            <DueBadge weeks={weeks} />
            <SourceBadge source={client.acquisition_source} />
            {client.sms_opt_out && <span className="badge badge-red">SMS opt-out</span>}
            {snoozed && (
              <span className="badge badge-muted">
                Snoozed until {fmtDate(client.rebook_snoozed_until)}
              </span>
            )}
          </div>
          {(gapWeeks != null || weeks != null) && (
            <div className="page-sub">
              {gapWeeks != null
                ? `Comes in about every ${gapWeeks} week${gapWeeks === 1 ? "" : "s"}`
                : "No visit rhythm yet"}
              {weeks != null
                ? `; last visit ${weeks === 0 ? "this week" : `${weeks} week${weeks === 1 ? "" : "s"} ago`}.`
                : "."}
            </div>
          )}
        </div>
        <div className="page-actions">
          <MarkReachedOut
            clientId={idArg}
            clientName={client.full_name}
            className="btn btn-ghost"
            onDone={() => {
              touchesQ.reload();
              remindersQ.reload();
            }}
          />
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setActionError(null);
              setPkgOpen(true);
            }}
          >
            Add package
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setActionError(null);
              setAdjOpen(true);
            }}
          >
            Add adjustment
          </button>
        </div>
      </div>

      {actionError && !adjOpen && !pkgOpen && <div className="error-banner">{actionError}</div>}

      <div className="card-grid">
        <div className="card">
          <div className="panel-title">Contact</div>
          <dl className="kv">
            <dt>Phone</dt>
            <dd>
              {client.phone_e164 ? (
                <span className="nowrap">
                  <a href={telHref(client.phone_e164)}>{phoneDisplay(client.phone_e164)}</a>
                  {" · "}
                  <a href={smsHref(client.phone_e164)}>SMS</a>
                </span>
              ) : (
                <span className="muted">—</span>
              )}
            </dd>
            <dt>Email</dt>
            <dd>
              {client.email ? (
                <a href={mailtoHref(client.email)}>{client.email}</a>
              ) : (
                <span className="muted">—</span>
              )}
            </dd>
            <dt>LTV</dt>
            <dd className="bold num">{money(num(client.ltv) ?? 0)}</dd>
            <dt>Visits</dt>
            <dd className="num">{num(client.visits) ?? 0}</dd>
            <dt>First visit</dt>
            <dd>{client.first_visit ? fmtDate(client.first_visit) : <span className="muted">—</span>}</dd>
            <dt>Last visit</dt>
            <dd>
              {client.last_visit ? (
                <>
                  {fmtDate(client.last_visit)}{" "}
                  <span className="muted small">({weeks}w ago)</span>
                </>
              ) : (
                <span className="muted">Never</span>
              )}
            </dd>
          </dl>
        </div>

        <div className="card">
          <div className="panel-title">Acquisition</div>
          <div className="field">
            <label htmlFor="acq-source">Source</label>
            <select
              id="acq-source"
              value={srcDraft}
              onChange={(e) => setSrcDraft(e.target.value)}
            >
              <option value="">Not set</option>
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="acq-note">Note</label>
            <input
              id="acq-note"
              value={srcNote}
              placeholder="e.g. referred by Jane D."
              onChange={(e) => setSrcNote(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={applyAcquisition}
            disabled={busy || !acquisitionDirty}
          >
            {busy ? "Saving…" : "Save acquisition"}
          </button>
        </div>

        <div className="card">
          <div className="panel-title">Tags &amp; rebook</div>
          <TagChips
            tags={client.tags ?? []}
            editable
            onAdd={(t) => addTag(t)}
            onRemove={(t) => removeTag(t)}
          />
          <hr />
          <div className="small muted mb-1">
            Rebook snooze hides this client from rebook lists and campaigns until the date.
          </div>
          <div className="flex flex-wrap">
            <input
              type="date"
              value={snoozeDraft}
              min={today}
              onChange={(e) => setSnoozeDraft(e.target.value)}
              aria-label="Snooze until date"
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => snoozeDraft && setSnooze(snoozeDraft)}
              disabled={busy || !snoozeDraft}
            >
              Snooze
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setSnooze(addWeeksYmd(today, 2))}
              disabled={busy}
            >
              +2w
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setSnooze(addWeeksYmd(today, 4))}
              disabled={busy}
            >
              +4w
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setSnooze(addWeeksYmd(today, 8))}
              disabled={busy}
            >
              +8w
            </button>
            {client.rebook_snoozed_until && (
              <button
                type="button"
                className="btn-link"
                onClick={() => setSnooze(null)}
                disabled={busy}
              >
                Clear snooze
              </button>
            )}
          </div>
        </div>

        <div className="card">
          <div className="panel-title">Credits</div>
          {creditsQ.error && (
            <div className="error-banner">Failed to load credits: {creditsQ.error}</div>
          )}
          {creditsQ.loading && <div className="loading">Loading credits…</div>}
          {creditsQ.data && creditsQ.data.length === 0 && (
            <div className="muted">
              No credits. Queue sends with a dollar offer file one automatically.
            </div>
          )}
          {(creditsQ.data ?? [])
            .filter((c) => c.status === "active")
            .map((c) => (
              <div key={c.id} className="mb-2">
                <div className="flex flex-wrap" style={{ alignItems: "baseline" }}>
                  <span className="bold num">{money(num(c.amount) ?? 0)}</span>
                  <span className="badge badge-amber">Active</span>
                  {c.expires_on && (
                    <span className={"small " + (c.expires_on < today ? "red" : "muted")}>
                      {c.expires_on < today ? "expired " : "expires "}
                      {fmtDate(c.expires_on)}
                    </span>
                  )}
                </div>
                <div className="small muted mt-1">{c.reason}</div>
                <div className="flex mt-1">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => resolveCredit(c.id, "redeemed")}
                    disabled={busy}
                    title="They used the credit on a visit"
                  >
                    Mark redeemed
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => resolveCredit(c.id, "revoked")}
                    disabled={busy}
                    title="Withdraw the credit — it was never used"
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))}
          {(creditsQ.data ?? []).some((c) => c.status !== "active") && (
            <>
              <div className="small muted mb-1">History</div>
              {(creditsQ.data ?? [])
                .filter((c) => c.status !== "active")
                .slice(0, 6)
                .map((c) => (
                  <div className="small muted mb-1" key={c.id}>
                    {c.status === "redeemed" ? "✓" : "✕"} {money(num(c.amount) ?? 0)} —{" "}
                    {c.status}
                    {c.redeemed_at ? ` ${fmtDate(c.redeemed_at)}` : ""} · {c.reason}
                  </div>
                ))}
            </>
          )}
        </div>
      </div>

      <div className="panel mt-2">
        <div className="flex mb-1">
          <div className="panel-title mt-0" style={{ marginBottom: 0 }}>
            Packages
          </div>
          <span className="flex-1" />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setActionError(null);
              setPkgOpen(true);
            }}
          >
            Add package
          </button>
        </div>
        {packagesQ.error && (
          <div className="error-banner">Failed to load packages: {packagesQ.error}</div>
        )}
        {packagesQ.loading && <div className="loading">Loading packages…</div>}
        {packagesQ.data && packagesQ.data.packages.length === 0 && (
          <div className="muted">
            No packages yet — use “Add package” to record one sold outside Mangomint.
          </div>
        )}
        {packagesQ.data?.packages.map((p) => {
          const entries = (packagesQ.data?.ledger ?? []).filter(
            (l) => String(l.package_id) === String(p.id),
          );
          const balance = entries.reduce((sum, l) => sum + (num(l.delta) ?? 0), 0);
          return (
            <div key={String(p.id)} className="mb-2">
              <div className="flex flex-wrap mb-1">
                <span className="bold">{p.drips_total}-drip package</span>
                {p.active ? (
                  <span className="badge badge-green">Active</span>
                ) : (
                  <span className="badge badge-muted">Inactive</span>
                )}
                <PackageBadge balance={balance} />
                {p.active && balance <= 1 && (
                  <span className="amber small bold">Low balance</span>
                )}
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>
                        <span className="th-sort" style={{ cursor: "default" }}>
                          Date
                        </span>
                      </th>
                      <th>
                        <span className="th-sort" style={{ cursor: "default" }}>
                          Reason
                        </span>
                      </th>
                      <th className="right">
                        <span className="th-sort" style={{ cursor: "default" }}>
                          Δ
                        </span>
                      </th>
                      <th>
                        <span className="th-sort" style={{ cursor: "default" }}>
                          Note
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 && (
                      <tr>
                        <td className="empty-cell" colSpan={4}>
                          No ledger entries
                        </td>
                      </tr>
                    )}
                    {entries.map((l, i) => {
                      const delta = num(l.delta) ?? 0;
                      return (
                        <tr key={l.id != null ? String(l.id) : `${String(p.id)}-${i}`}>
                          <td className="nowrap">{fmtDateTime(l.created_at)}</td>
                          <td>{REASON_LABELS[l.reason] ?? l.reason}</td>
                          <td className={"right " + (delta < 0 ? "red" : "green")}>
                            {delta > 0 ? `+${delta}` : delta}
                          </td>
                          <td>{l.note ?? <span className="muted">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      <div className="panel mt-2">
        <div className="flex mb-1">
          <div className="panel-title mt-0" style={{ marginBottom: 0 }}>
            Sale history
          </div>
          <span className="flex-1" />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setActionError(null);
              setAdjOpen(true);
            }}
          >
            Add adjustment
          </button>
        </div>
        <div className="small muted mb-1">
          Sales are append-only. Corrections are new adjustment rows — existing rows are never
          edited.
        </div>
        {salesQ.error && (
          <div className="error-banner">Failed to load sales: {salesQ.error}</div>
        )}
        {salesQ.loading ? (
          <div className="loading">Loading sales…</div>
        ) : (
          <DataTable<Sale>
            columns={SALE_COLUMNS}
            rows={salesQ.data ?? []}
            defaultSortKey="sale_date"
            defaultSortDir="desc"
            emptyMessage="No sales yet"
          />
        )}
      </div>

      <div className="panel mt-2">
        <div className="flex mb-1">
          <div className="panel-title mt-0" style={{ marginBottom: 0 }}>
            Outreach
          </div>
          <span className="flex-1" />
          <MarkReachedOut
            clientId={idArg}
            clientName={client.full_name}
            onDone={() => {
              touchesQ.reload();
              remindersQ.reload();
            }}
          />
        </div>
        <div className="small muted mb-1">
          Manual touches logged with “✓ Reached out” — texts sent from the CRM count automatically.
        </div>
        {touchesQ.error && (
          <div className="error-banner">Failed to load outreach: {touchesQ.error}</div>
        )}
        {touchesQ.loading && <div className="loading">Loading outreach…</div>}
        {touchesQ.data && touchesQ.data.length === 0 && (
          <div className="muted">No manual outreach logged yet.</div>
        )}
        {touchesQ.data && touchesQ.data.length > 0 && (
          <div className="action-list">
            {touchesQ.data.map((t) => (
              <div className="action-item" key={t.id}>
                <div className="action-main">
                  <div className="action-name">
                    <TouchChannelBadge channel={t.channel} />{" "}
                    <span className="muted small">
                      {daysAgoLabel(t.touched_at)} · {fmtDateTime(t.touched_at)}
                    </span>
                  </div>
                  {t.note && <div className="action-reason">{t.note}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Reminders</div>
        <div className="flex flex-wrap mb-2">
          <input
            type="date"
            value={remDate}
            min={today}
            onChange={(e) => setRemDate(e.target.value)}
            aria-label="Reminder due date"
          />
          <input
            value={remNote}
            placeholder="Remind me to…"
            style={{ flex: 1, minWidth: 180 }}
            onChange={(e) => setRemNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addReminder();
              }
            }}
            aria-label="Reminder note"
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={addReminder}
            disabled={busy || !remNote.trim() || !remDate}
          >
            Add reminder
          </button>
        </div>
        {remindersQ.error && (
          <div className="error-banner">Failed to load reminders: {remindersQ.error}</div>
        )}
        {remindersQ.loading && <div className="loading">Loading reminders…</div>}
        {remindersQ.data && remindersQ.data.length === 0 && (
          <div className="muted">No reminders for this client.</div>
        )}
        {pendingReminders.length > 0 && (
          <div className="action-list mb-2">
            {pendingReminders.map((r) => {
              const overdue = r.due_date < today;
              return (
                <div className={"action-item" + (overdue ? " overdue" : "")} key={r.id}>
                  <div className="action-main">
                    <div className="action-name">
                      {overdue ? "Overdue — " : "Due "}
                      {fmtDate(r.due_date)}
                      {r.source !== "manual" && (
                        <span className="muted small"> · {r.source === "ai_note" ? "AI" : "system"}</span>
                      )}
                    </div>
                    <div className="action-reason">{r.note}</div>
                  </div>
                  <div className="action-buttons">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => completeReminder(r.id, "done")}
                      disabled={busy}
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => completeReminder(r.id, "dismissed")}
                      disabled={busy}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {doneReminders.length > 0 && (
          <>
            <div className="small muted mb-1">Completed</div>
            {doneReminders.slice(0, 8).map((r) => (
              <div className="small muted mb-1" key={r.id}>
                {r.status === "done" ? "✓" : "✕"} {fmtDate(r.due_date)} — {r.note}
                {r.completed_at ? ` (${r.status} ${fmtDate(r.completed_at)})` : ""}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Notes</div>
        <div className="field">
          <textarea
            rows={2}
            value={noteDraft}
            placeholder="Add a note…"
            onChange={(e) => setNoteDraft(e.target.value)}
            aria-label="New note"
          />
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm mb-2"
          onClick={addNote}
          disabled={busy || !noteDraft.trim()}
        >
          Add note
        </button>
        {notesQ.error && (
          <div className="error-banner">Failed to load notes: {notesQ.error}</div>
        )}
        {notesQ.loading && <div className="loading">Loading notes…</div>}
        {notesQ.data && notesQ.data.length === 0 && <div className="muted">No notes yet.</div>}
        {notesQ.data?.map((n) =>
          editingNoteId === String(n.id) ? (
            <div key={String(n.id)} className="card mb-1">
              <div className="field">
                <textarea
                  rows={3}
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  aria-label="Edit note"
                />
              </div>
              <div className="flex">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => saveNoteEdit(n.id)}
                  disabled={busy || !editDraft.trim()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setEditingNoteId(null);
                    setEditDraft("");
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div key={String(n.id)} className="card mb-1">
              <div style={{ whiteSpace: "pre-wrap" }}>{n.body}</div>
              <div className="flex mt-1 small muted">
                <span>
                  {fmtDateTime(n.created_at)}
                  {noteEdited(n) ? " · edited" : ""}
                </span>
                <span className="flex-1" />
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => {
                    setEditingNoteId(String(n.id));
                    setEditDraft(n.body);
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="btn-link"
                  style={{ color: "var(--red)" }}
                  onClick={() => deleteNote(n.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ),
        )}
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Messages</div>
        <MessageThread
          clientId={client.id}
          phone={client.phone_e164}
          optedOut={client.sms_opt_out}
          compose={compose}
        />
      </div>

      <Modal
        open={adjOpen}
        title="Add adjustment"
        onClose={() => setAdjOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setAdjOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitAdjustment}
              disabled={busy || !adjNote.trim() || adjAmount.trim() === ""}
            >
              {busy ? "Saving…" : "Add adjustment"}
            </button>
          </>
        }
      >
        {actionError && <div className="error-banner">{actionError}</div>}
        <div className="info-banner">
          Adds a new <span className="bold">adjustment</span> sales row (sales are append-only).
          Use a negative amount to correct over-collected revenue.
        </div>
        <div className="form-row">
          <div className="field">
            <label htmlFor="adj-amount">Amount ($)</label>
            <input
              id="adj-amount"
              type="number"
              step="0.01"
              value={adjAmount}
              placeholder="-50.00"
              onChange={(e) => setAdjAmount(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="adj-date">Date (optional)</label>
            <input
              id="adj-date"
              type="date"
              value={adjDate}
              onChange={(e) => setAdjDate(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label htmlFor="adj-note">Note (required)</label>
          <input
            id="adj-note"
            value={adjNote}
            placeholder="Why this adjustment exists"
            onChange={(e) => setAdjNote(e.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={pkgOpen}
        title="Add package"
        onClose={() => setPkgOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setPkgOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitPackage}
              disabled={busy || pkgTotal.trim() === ""}
            >
              {busy ? "Saving…" : "Add package"}
            </button>
          </>
        }
      >
        {actionError && <div className="error-banner">{actionError}</div>}
        <div className="info-banner">
          Records a package with a ledger grant (and marks any already-used drips). This never
          creates a sale — revenue stays exactly as Mangomint reported it.
        </div>
        <div className="form-row">
          <div className="field">
            <label htmlFor="pkg-total">Drips total</label>
            <input
              id="pkg-total"
              type="number"
              min={1}
              step={1}
              value={pkgTotal}
              placeholder="e.g. 5"
              onChange={(e) => setPkgTotal(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="pkg-used">Already used</label>
            <input
              id="pkg-used"
              type="number"
              min={0}
              step={1}
              value={pkgUsed}
              onChange={(e) => setPkgUsed(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label htmlFor="pkg-note">Note (optional)</label>
          <input
            id="pkg-note"
            value={pkgNote}
            placeholder="e.g. 5-pack sold 6/12, 2 used before CRM"
            onChange={(e) => setPkgNote(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
